/**
 * The exact message the server returns when a master-admin action is attempted
 * while an impersonation session is active.  Kept in sync with
 * MASTER_ADMIN_IMPERSONATION_MESSAGE in api-server/src/replitAuth.ts.
 */
export const MASTER_ADMIN_IMPERSONATION_PHRASE =
  "Stop impersonating to use master-admin actions";

/**
 * Returns true when the error was caused by attempting a master-admin action
 * while an impersonation session is active.  The server returns HTTP 403 with
 * a message containing MASTER_ADMIN_IMPERSONATION_PHRASE in that case.
 */
export function isMasterAdminImpersonationError(error: unknown): boolean {
  const message = String((error as any)?.message ?? "");
  return message.includes(MASTER_ADMIN_IMPERSONATION_PHRASE);
}

/**
 * Returns true when an error looks like a 403 / edit-permission rejection from
 * the server.  Centralised here so the detection can't drift between call sites.
 *
 * Detection covers two shapes that apiRequest can produce:
 *  1. The HTTP status code is attached as `error.status === 403` (primary check).
 *  2. The message string embeds the code or a phrase such as "edit permission"
 *     or "forbidden" (fallback for wrapped / rethrown errors).
 *
 * Callers should show a "View Only" notice instead of a raw error.
 */
export function isEditPermissionError(error: unknown): boolean {
  if ((error as any)?.status === 403) return true;
  const message = String((error as any)?.message ?? "");
  return /\b403\b/.test(message) || /edit permission|forbidden/i.test(message);
}

/**
 * Copy shown when a reorder batch is rejected because the caller's edit
 * access to the shared task was revoked (downgraded to view) mid-session.
 */
export const REVOKED_EDIT_ACCESS_MESSAGE =
  "Your edit access to this task was removed.";

/**
 * Returns true when a reorder batch was rejected specifically because the
 * caller's edit access was revoked mid-session: the server answers 403 AND
 * includes a skipped-IDs array naming the items it refused to move —
 * `skippedSubTaskIds` from the sub-task reorder endpoint or
 * `skippedMilestoneIds` from the milestone reorder endpoint (attached to the
 * error by apiRequest). Plain 403s without either field (e.g. a task that was
 * never editable) stay on the generic view-only copy.
 */
export function isRevokedEditAccessError(error: unknown): boolean {
  return (
    (error as any)?.status === 403 &&
    (Array.isArray((error as any)?.skippedSubTaskIds) ||
      Array.isArray((error as any)?.skippedMilestoneIds))
  );
}

/**
 * Broader variant used for sub-task mutations.  Sub-task endpoints can also
 * return HTTP 404 with a "not found" or generic "permission" message when the
 * caller lacks access to the parent task, so those cases are also treated as
 * view-only failures rather than generic errors.
 *
 * Detection order:
 *  1. `error.status === 403` or `error.status === 404` (primary — set by apiRequest).
 *  2. Message text contains the status code or a permission phrase (fallback for
 *     wrapped / rethrown errors where the status property is no longer present).
 */
export function isSubtaskPermissionError(error: unknown): boolean {
  const status = (error as any)?.status;
  if (status === 403 || status === 404) return true;
  const message = String((error as any)?.message ?? "");
  return (
    /\b(403|404)\b/.test(message) ||
    /not found|permission|forbidden/i.test(message)
  );
}
